/**
 * One Story's reviews (TargetType 10), newest first, with hide/unhide and removal.
 *
 * Hiding (reason required) is the reversible moderation action: the review leaves the public lists and the Story's
 * rating, and its author still sees it with the reason. Removal is permanent. Both go through the user data
 * editor's pipeline (Backlog 050), which records the reason on the reviewer's audit history and refreshes the
 * Story's cached rating. The Story is chosen with `?story=` so a row on the Stories tab can link here.
 */

import { useMemo, useState } from 'react'
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { Delete, Visibility, VisibilityOff } from '@mui/icons-material'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import type { RowAction } from '@components/common/RowActions'
import { useDebouncedValue } from '@hooks/useDebouncedValue'
import { storiesApi } from '@api/endpoints/stories'
import type { AdminStoryReviewDto, AdminStoryRowDto } from '@api/types/stories'
import { errorMessage, formatDay } from '@features/admin/components/users/userFormatting'
import { UserDataActionDialog } from '@features/admin/components/users/UserDataActionDialog'
import { UserDataChangeResultDialog } from '@features/admin/components/users/UserDataChangeResultDialog'
import { useUserDataChange } from '@features/admin/components/users/useUserDataChange'
import { HiddenReviewChip, ReviewHideDialog } from '@features/admin/components/users/ReviewHideControls'
import type { ReviewVisibilityFilter } from '@api/types/stories'
import { STORIES_KEY, storyReviewsKey } from '../storyQueryKeys'

const progressLabel = (r: AdminStoryReviewDto) =>
  r.reviewerProgress.finished
    ? 'Finished'
    : `${r.reviewerProgress.completedChapters}/${r.reviewerProgress.totalChapters} ch.`

export function StoryReviewsTab() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const storyId = searchParams.get('story') ?? ''
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [target, setTarget] = useState<AdminStoryReviewDto | null>(null)
  const [action, setAction] = useState<'delete' | 'hide'>('delete')
  const [visibility, setVisibility] = useState<ReviewVisibilityFilter>('all')

  // The picker searches the server as you type: any user can make a Story, so "one page of 100
  // is all of them" stops being true, and a Story past the 100th could not be picked at all.
  const location = useLocation()
  const [pickerInput, setPickerInput] = useState('')
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null)
  const pickerSearch = useDebouncedValue(pickerInput.trim(), 300)
  const { data: stories, isFetching: storiesLoading } = useQuery({
    queryKey: [...STORIES_KEY, 'picker', pickerSearch],
    queryFn: () => storiesApi.list({ search: pickerSearch || undefined, sort: 'name', page: 1, pageSize: 25 }),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  })
  // The chosen Story's name: from the pick, from the search results, or handed over by the Stories
  // tab's Reviews action (there is no get-one-Story read to look it up by id).
  const stateName = (location.state as { storyName?: string } | null)?.storyName
  type StoryOption = { id: string; name: string; row?: AdminStoryRowDto }
  const selected = useMemo((): StoryOption | null => {
    if (!storyId) return null
    if (picked?.id === storyId) return picked
    const found = stories?.items.find((s) => s.id === storyId)
    if (found) return { id: found.id, name: found.name }
    return { id: storyId, name: stateName || 'Selected story' }
  }, [storyId, picked, stories, stateName])
  const options = useMemo(() => {
    const items: StoryOption[] = (stories?.items ?? []).map((s) => ({ id: s.id, name: s.name, row: s }))
    if (selected && !items.some((o) => o.id === selected.id)) items.unshift(selected)
    return items
  }, [stories, selected])

  const { data, isLoading, error } = useQuery({
    queryKey: [...storyReviewsKey(storyId), page, pageSize, visibility],
    queryFn: () => storiesApi.getReviews(storyId, page + 1, pageSize, visibility),
    enabled: Boolean(storyId),
  })

  // The reviewer's id keys the change hook's own invalidation; the Story's lists are invalidated here too.
  const change = useUserDataChange(target?.userId ?? '')

  const pickStory = (id: string) => {
    const next = new URLSearchParams(searchParams)
    if (id) next.set('story', id)
    else next.delete('story')
    setSearchParams(next, { replace: true })
    setPage(0)
  }

  const reviewActions = (r: AdminStoryReviewDto): RowAction[] => [
    {
      key: 'hide',
      label: r.isHidden ? 'Unhide' : 'Hide',
      hint: r.isHidden ? 'Unhide review' : 'Hide review',
      icon: r.isHidden ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />,
      onClick: () => {
        setAction('hide')
        setTarget(r)
      },
    },
    {
      key: 'remove',
      label: 'Remove',
      hint: 'Remove review',
      icon: <Delete fontSize="small" />,
      color: 'error',
      onClick: () => {
        setAction('delete')
        setTarget(r)
      },
    },
  ]

  const close = () => {
    setTarget(null)
    change.resetError()
  }

  const columns: DataTableColumn<AdminStoryReviewDto>[] = [
    {
      key: 'reviewer',
      header: 'Reviewer',
      primary: true,
      render: (r) => (
        <Link component={RouterLink} to={`/users/${r.userId}`} underline="hover" sx={{ wordBreak: 'break-word' }}>
          {r.userDisplayName || r.userEmail || r.userId.slice(0, 8)}
        </Link>
      ),
    },
    { key: 'stars', header: 'Stars', align: 'right', render: (r) => (r.stars != null ? r.stars.toFixed(1) : '—') },
    {
      key: 'comment',
      header: 'Comment',
      render: (r) =>
        r.comment ? (
          <Typography
            variant="body2"
            component="span"
            sx={{ display: 'block', maxWidth: { sm: 360 }, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
          >
            {r.comment}
          </Typography>
        ) : (
          '—'
        ),
    },
    {
      key: 'flags',
      header: 'Flags',
      render: (r) =>
        r.containsSpoilers || r.isPrivateNote || r.isHidden ? (
          <Box sx={{ display: 'inline-flex', gap: 0.5, flexWrap: 'wrap' }}>
            <HiddenReviewChip review={r} />
            {r.containsSpoilers && <Chip size="small" color="warning" variant="outlined" label="Spoilers" />}
            {r.isPrivateNote && <Chip size="small" color="secondary" variant="outlined" label="Private note" />}
          </Box>
        ) : (
          '—'
        ),
    },
    { key: 'progress', header: 'Reviewer progress', render: progressLabel },
    { key: 'date', header: 'Written', hideBelow: 'md', render: (r) => formatDay(r.createdAt) },
  ]

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
      <Autocomplete
        size="small"
        sx={{ width: { xs: '100%', sm: 360 } }}
        options={options}
        value={selected}
        onChange={(_, option) => {
          setPicked(option ? { id: option.id, name: option.name } : null)
          pickStory(option?.id ?? '')
        }}
        inputValue={pickerInput}
        onInputChange={(_, value, reason) => {
          // Picking fills the box with the name; that is not a new search.
          if (reason !== 'reset') setPickerInput(value)
        }}
        // The server already filtered by name, area and tagline.
        filterOptions={(o) => o}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        loading={storiesLoading}
        noOptionsText={pickerSearch ? 'No story matches' : 'No stories'}
        renderOption={(props, o) => (
          <li {...props} key={o.id}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                {o.name}
              </Typography>
              {o.row && (
                <Typography variant="caption" color="text.secondary">
                  {[!o.row.isPublic ? 'hidden' : null, `${o.row.reviewCount} review${o.row.reviewCount === 1 ? '' : 's'}`]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              )}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Story"
            placeholder="Search stories"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {storiesLoading && <CircularProgress size={16} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      <FormControl size="small" sx={{ width: { xs: '100%', sm: 160 } }}>
        <InputLabel>Show</InputLabel>
        <Select
          label="Show"
          value={visibility}
          onChange={(e) => {
            setVisibility(e.target.value as ReviewVisibilityFilter)
            setPage(0)
          }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="visible">Visible</MenuItem>
          <MenuItem value="hidden">Hidden</MenuItem>
        </Select>
      </FormControl>
      </Box>

      {!storyId ? (
        <Alert severity="info">Choose a story to see its reviews.</Alert>
      ) : (
        <DataTable
          rows={data?.items ?? []}
          columns={columns}
          getRowKey={(r) => r.id}
          loading={isLoading}
          error={error ? errorMessage(error, 'Failed to load reviews') : null}
          emptyMessage="No reviews of this story"
          dense
          page={page}
          pageSize={pageSize}
          total={data?.totalCount ?? 0}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size)
            setPage(0)
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          pagerPosition="both"
          getRowActions={reviewActions}
        />
      )}

      {target && action === 'hide' && (
        <ReviewHideDialog
          review={target}
          description={`${target.isPrivateNote ? 'Private note' : 'Review'} by ${target.userDisplayName || target.userEmail || 'this user'}, written ${formatDay(target.createdAt)}`}
          pending={change.pending}
          error={change.error}
          submit={change.submit}
          onDone={() => {
            queryClient.invalidateQueries({ queryKey: STORIES_KEY })
            close()
          }}
          onClose={close}
        />
      )}

      {target && action === 'delete' && (
        <UserDataActionDialog
          title="Remove review?"
          message={`${target.isPrivateNote ? 'Private note' : 'Review'} by ${target.userDisplayName || target.userEmail || 'this user'}, written ${formatDay(target.createdAt)}. This cannot be undone.`}
          confirmLabel="Remove review"
          destructive
          pending={change.pending}
          error={change.error}
          onSubmit={(reason) =>
            change.submit(() => storiesApi.deleteReview(storyId, target.id, reason), () => {
              queryClient.invalidateQueries({ queryKey: STORIES_KEY })
              close()
            })
          }
          onClose={close}
        />
      )}

      <UserDataChangeResultDialog open={change.resultOpen} result={change.result} onClose={change.closeResult} />
    </>
  )
}
